import type { Policy, CreatePolicyInput, PolicyLabel } from '@communities/content-management/domain';
import { useCreateCommunityPolicy, useUpdateCommunityPolicy } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { LabelWithScoreThresholdSelector, type ScoreLabelOption, type LabelWithScore } from '@core/components/LabelWithScoreThresholdSelector';
import { LabelWithSeveritySelector, type LabelOption, type LabelWithSeverity } from '@core/components/LabelWithSeveritySelector';
import { LoadingSpinner, TextInput, TextArea, Dialog } from '@soteria/design-system-ve';
import { ContentType } from '@system-config/label-management/domain';
import { useStandardLabels } from '@system-config/label-management/hooks';
import { useState, useEffect, useCallback, useMemo } from 'react';

import * as styles from './PolicyDialog.css';

interface PolicyDialogProps {
  policy?: Policy | undefined;
  communityId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Sanitizes labels by trimming labelId and removing Apollo's __typename
 */
function sanitizePolicyLabel(label: PolicyLabel): PolicyLabel {
  const { __typename, ...cleanLabel } = label as PolicyLabel & { __typename?: string };

  return {
    ...cleanLabel,
    labelId: cleanLabel.labelId.trim(),
  };
}

/**
 * Checks if a label is a text label (has severity)
 */
function isTextLabel(label: PolicyLabel): boolean {
  return typeof label.severity === 'number' && (label.scoreThreshold === undefined || label.scoreThreshold === null);
}

/**
 * Checks if a label is an image label (has scoreThreshold)
 */
function isImageLabel(label: PolicyLabel): boolean {
  return typeof label.scoreThreshold === 'number' && (label.severity === undefined || label.severity === null);
}

function usePolicyForm(initialPolicy: Policy | undefined) {
  const [currentPolicy, setCurrentPolicy] = useState<Partial<Policy> & { labels: PolicyLabel[] }>({
    name: '',
    description: '',
    isEnabled: true,
    labels: [],
  });

  const resetForm = useCallback(() => {
    if (initialPolicy) {
      setCurrentPolicy({
        name: initialPolicy.name,
        description: initialPolicy.description || '',
        isEnabled: initialPolicy.isEnabled,
        labels: initialPolicy.labels,
      });
    } else {
      setCurrentPolicy({
        name: '',
        description: '',
        isEnabled: true,
        labels: [],
      });
    }
  }, [initialPolicy]);

  const handleFieldChange = useCallback((field: keyof Policy, value: unknown) => {
    setCurrentPolicy(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  return {
    currentPolicy,
    resetForm,
    handleFieldChange,
  };
}

// eslint-disable-next-line complexity
export function PolicyDialog({ policy: initialPolicy, communityId, isOpen, onClose }: PolicyDialogProps) {
  const { currentPolicy, resetForm, handleFieldChange } = usePolicyForm(initialPolicy);

  const createHook = useCreateCommunityPolicy(communityId);
  const updateHook = useUpdateCommunityPolicy(communityId);
  const { labels: standardLabels, loading: labelsLoading } = useStandardLabels();

  const { createPolicy, loading: creating, errors: createErrors, resetErrors: resetCreateErrors } = createHook;
  const { updatePolicy, loading: updating, errors: updateErrors, resetErrors: resetUpdateErrors } = updateHook;

  const mutating = creating || updating;
  const errors = createErrors || updateErrors;
  const resetErrors = initialPolicy ? resetUpdateErrors : resetCreateErrors;

  // Separate text and image labels
  const textLabelOptions: LabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.TEXT)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
          maxSeverity: label.maxSeverity,
        })),
    [standardLabels]
  );

  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.IMAGE)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  // Transform PolicyLabel[] to LabelWithSeverity[] (text labels only)
  // Only include labels with valid severity value (not null, not undefined, >= 1)
  const textLabelsWithSeverity: LabelWithSeverity[] = useMemo(
    () =>
      (currentPolicy.labels || [])
        .filter(label => typeof label.severity === 'number' && label.severity >= 1)
        .map(label => ({
          labelId: label.labelId,
          severity: label.severity ?? 0,
        })),
    [currentPolicy.labels]
  );

  // Transform PolicyLabel[] to LabelWithScore[] (image labels only)
  // Only include labels with valid scoreThreshold value (not null, not undefined, >= 0)
  const imageLabelsWithScore: LabelWithScore[] = useMemo(
    () =>
      (currentPolicy.labels || [])
        .filter(label => typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0)
        .map(label => ({
          labelId: label.labelId,
          scoreThreshold: label.scoreThreshold ?? 0,
        })),
    [currentPolicy.labels]
  );

  // Handle text label changes
  const handleTextLabelsChange = useCallback(
    (labels: LabelWithSeverity[]) => {
      const textPolicyLabels: PolicyLabel[] = labels.map(label => ({
        labelId: label.labelId,
        severity: label.severity,
      }));

      // Keep existing image labels
      const imagePolicyLabels = currentPolicy.labels.filter(isImageLabel);

      handleFieldChange('labels', [...textPolicyLabels, ...imagePolicyLabels]);
    },
    [handleFieldChange, currentPolicy.labels]
  );

  // Handle image label changes
  const handleImageLabelsChange = useCallback(
    (labels: LabelWithScore[]) => {
      const imagePolicyLabels: PolicyLabel[] = labels.map(label => ({
        labelId: label.labelId,
        scoreThreshold: label.scoreThreshold,
      }));

      // Keep existing text labels
      const textPolicyLabels = currentPolicy.labels.filter(isTextLabel);

      handleFieldChange('labels', [...textPolicyLabels, ...imagePolicyLabels]);
    },
    [handleFieldChange, currentPolicy.labels]
  );

  useEffect(() => {
    if (isOpen) {
      resetErrors();
      resetForm();
    }
  }, [initialPolicy, isOpen, resetErrors, resetForm]);

  // Helper function to validate policy labels
  // Must have either severity (text labels) or scoreThreshold (image labels)
  function isValidPolicyLabel(label: PolicyLabel | null | undefined): label is PolicyLabel {
    if (!label || typeof label.labelId !== 'string' || label.labelId.trim() === '') {
      return false;
    }

    // Text label: must have severity >= 1
    const hasValidSeverity = typeof label.severity === 'number' && label.severity >= 1;

    // Image label: must have scoreThreshold >= 0
    const hasValidScoreThreshold = typeof label.scoreThreshold === 'number' && label.scoreThreshold >= 0;

    // Ensure mutual exclusivity between severity and scoreThreshold
    return (hasValidSeverity && !hasValidScoreThreshold) || (hasValidScoreThreshold && !hasValidSeverity);
  }

  const handleSubmit = async () => {
    resetErrors();

    // Filter and sanitize labels
    const validLabels = currentPolicy.labels.filter(isValidPolicyLabel).map(sanitizePolicyLabel);

    const input: CreatePolicyInput = {
      name: currentPolicy.name?.trim() || '',
      description: currentPolicy.description?.trim(),
      isEnabled: currentPolicy.isEnabled ?? true,
      labels: validLabels,
    };

    const success = initialPolicy ? await updatePolicy(communityId, initialPolicy.id, input) : await createPolicy(communityId, input);

    if (success) {
      onClose();
    }
  };

  return (
    <Dialog
      description={initialPolicy ? 'Update policy settings' : 'Configure a new policy'}
      isOpen={isOpen}
      primaryAction={{
        label: mutating ? <LoadingSpinner /> : initialPolicy ? 'Update Policy' : 'Create Policy',
        onClick: handleSubmit,
        disabled: mutating || !currentPolicy.name?.trim() || labelsLoading,
      }}
      size="xl"
      title={initialPolicy ? 'Edit Policy' : 'Add Policy'}
      onClose={onClose}
    >
      <div className={styles.formContainer}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the policy:" />}

        {/* Name Input */}
        <TextInput
          required
          id="policyName"
          label="Policy Name"
          placeholder="Enter policy name"
          value={currentPolicy.name || ''}
          onChange={value => handleFieldChange('name', value)}
        />

        {/* Description Input */}
        <TextArea
          id="description"
          placeholder="Enter policy description"
          title="Description"
          value={currentPolicy.description || ''}
          onChange={e => handleFieldChange('description', e.target.value)}
        />

        {/* Enabled Checkbox */}
        <div className={styles.checkboxContainer}>
          <input checked={currentPolicy.isEnabled ?? true} id="isEnabled" type="checkbox" onChange={e => handleFieldChange('isEnabled', e.target.checked)} />
          <label htmlFor="isEnabled">Policy Enabled</label>
        </div>

        {/* Text Labels Selector */}
        {textLabelOptions.length > 0 && (
          <LabelWithSeveritySelector
            showSeverityDescription
            availableLabels={textLabelOptions}
            defaultMaxSeverity={3}
            defaultSeverity={2}
            emptyStateMessage="No text labels selected. Add labels to define text content criteria."
            loading={labelsLoading}
            selectedLabels={textLabelsWithSeverity}
            title="Text Content Labels"
            onChange={handleTextLabelsChange}
          />
        )}

        {/* Image Labels Selector */}
        {imageLabelOptions.length > 0 && (
          <LabelWithScoreThresholdSelector
            showScoreDescription
            availableLabels={imageLabelOptions}
            defaultScoreThreshold={70}
            emptyStateMessage="No image labels selected. Add labels to define image content criteria."
            loading={labelsLoading}
            maxScore={100}
            minScore={0}
            selectedLabels={imageLabelsWithScore}
            title="Image Content Labels"
            onChange={handleImageLabelsChange}
          />
        )}
      </div>
    </Dialog>
  );
}
